import { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';

// Context type definition
interface ProgressContextType {
  // Current position in milliseconds
  progressMs: number;
  
  // Set progress (for manual updates)
  setProgressMs: (ms: number) => void;
  
  // Set progress and start/update the timer
  startProgressTimer: (startMs: number, isPlaying: boolean, timestamp?: number) => void;
  
  // Reset for new track with fresh timing data
  resetForNewTrack: (newTrackId: string, startMs: number, isPlaying: boolean, timestamp?: number) => void;
  
  // Reset timing without changing track ID (for drift correction)
  resetTimingForSameTrack: (startMs: number, isPlaying: boolean, timestamp?: number) => void;
  
  // For manual lyric selection
  manualSelectedLyric: string | null;
  manualLyricTimestamp: number | null;
  setManualLyricSelection: (lyric: string | null, timestamp: number | null) => void;
  
  // Flag for if user is manually scrolling
  userScrolling: boolean;
  setUserScrolling: (scrolling: boolean) => void;
  
  // For immediately clearing manual states
  clearManualStates: (preserveDelay?: boolean) => void;
  
  // Flag for if track is playing
  isPlaying: boolean;
  
  // When a track changes
  currentTrackId: string | null;
  setCurrentTrackId: (id: string | null) => void;
  trackIdJustChanged: boolean;
  
  // Lyrics timing adjustment
  lyricsDelay: number;
  setLyricsDelay: (delayMs: number) => void;
  
  // Get the effective progress with delay applied
  getEffectiveProgress: () => number;
  
  // Check if we're in any kind of manual mode
  isInManualMode: () => boolean;
}

// Create context with default values
const ProgressContext = createContext<ProgressContextType>({
  progressMs: 0,
  setProgressMs: () => {
    console.warn('ProgressContext not initialized');
  },
  startProgressTimer: () => {
    console.warn('ProgressContext not initialized');
  },
  resetForNewTrack: () => {
    console.warn('ProgressContext not initialized');
  },
  resetTimingForSameTrack: () => {
    console.warn('ProgressContext not initialized');
  },
  manualSelectedLyric: null,
  manualLyricTimestamp: null,
  setManualLyricSelection: () => {
    console.warn('ProgressContext not initialized');
  },
  userScrolling: false,
  setUserScrolling: () => {
    console.warn('ProgressContext not initialized');
  },
  clearManualStates: () => {
    console.warn('ProgressContext not initialized');
  },
  isPlaying: false,
  currentTrackId: null,
  setCurrentTrackId: () => {
    console.warn('ProgressContext not initialized');
  },
  trackIdJustChanged: false,
  lyricsDelay: 0,
  setLyricsDelay: () => {
    console.warn('ProgressContext not initialized');
  },
  getEffectiveProgress: () => {
    console.warn('ProgressContext not initialized');
    return 0;
  },
  isInManualMode: () => false
});

// Provider component
export function ProgressProvider({ 
  children, 
  initialTrackDuration = 0
}: { 
  children: ReactNode,
  initialTrackDuration?: number 
}) {
  // State for tracking progress
  const [progressMs, setProgressMs] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const progressInterval = useRef<number | null>(null);
  const trackDuration = useRef<number>(initialTrackDuration);
  
  // Reference to store the last Spotify API response timestamp
  const lastResponseTimestamp = useRef<number | null>(null);
  const progressStartTime = useRef<number | null>(null);
  
  // State for manual lyric selection
  const [manualSelectedLyric, setManualSelectedLyric] = useState<string | null>(null);
  const [manualLyricTimestamp, setManualLyricTimestamp] = useState<number | null>(null);
  
  // State for user scrolling
  const [userScrolling, setUserScrolling] = useState<boolean>(false);
  
  // State for lyrics timing delay
  const [lyricsDelay, setLyricsDelay] = useState<number>(0);
  
  // State for track ID change detection
  const [trackIdJustChanged, setTrackIdJustChanged] = useState<boolean>(false);
  
  // Track ID reference for detecting track changes
  const [currentTrackId, setCurrentTrackId] = useState<string | null>(null);
  
  // Function to get effective progress with delay applied
  const getEffectiveProgress = useCallback(() => {
    return progressMs + lyricsDelay;
  }, [progressMs, lyricsDelay]);
  
  // Function to check if we're in any kind of manual mode
  const isInManualMode = useCallback(() => {
    return manualSelectedLyric !== null || lyricsDelay !== 0;
  }, [manualSelectedLyric, lyricsDelay]);
  
  // Function to handle setting manual lyric selection
  const setManualLyricSelection = useCallback((lyric: string | null, timestamp: number | null) => {
    setManualSelectedLyric(lyric);
    setManualLyricTimestamp(timestamp);
    
    // If setting a manual selection, automatically update progress
    if (lyric !== null && timestamp !== null) {
      setProgressMs(timestamp);
    }
  }, []);
  
  // Function to clear all manual states
  const clearManualStates = useCallback((preserveDelay: boolean = false) => {
    setManualSelectedLyric(null);
    setManualLyricTimestamp(null);
    
    // Only reset lyrics delay if preserveDelay is false
    if (!preserveDelay) {
      setLyricsDelay(0);
    }
  }, []);
  
  // Function to start/update progress timer with optional timestamp from Spotify
  const startProgressTimer = useCallback((startMs: number, playing: boolean, timestamp?: number) => {
    // Clear any existing interval
    if (progressInterval.current) {
      window.clearInterval(progressInterval.current);
      progressInterval.current = null;
    }

    // If we have a Spotify timestamp, store it for future reference
    if (timestamp) {
      lastResponseTimestamp.current = timestamp;
      
      // We now use the timestamp directly for accurate synchronization
      // No need to add additional delays - the timestamp itself accounts for server timing
    }

    // Track when we started the timer for more accurate updates
    progressStartTime.current = Date.now();

    // Update state with new values
    setProgressMs(startMs);
    setIsPlaying(playing);

    // Start a new timer if playing
    if (playing) {
      progressInterval.current = window.setInterval(() => {
        setProgressMs(prev => {
          // Calculate a more accurate progress based on actual elapsed time
          if (progressStartTime.current) {
            const elapsed = Date.now() - progressStartTime.current;
            const calculatedProgress = startMs + elapsed;
            
            // Don't progress beyond track duration if we know it
            if (trackDuration.current > 0 && calculatedProgress >= trackDuration.current) {
              return trackDuration.current;
            }
            
            return calculatedProgress;
          }
          
          // Fallback to simple increment if we don't have start time
          if (trackDuration.current > 0 && prev >= trackDuration.current) {
            return trackDuration.current;
          }
          return prev + 1000;
        });
      }, 250); // Update more frequently (4 times per second) for smoother progress
    }
  }, []);
  
  // Update track duration when it changes
  const setTrackDuration = useCallback((duration: number) => {
    trackDuration.current = duration;
  }, []);
  
  // Custom function to set current track ID with change detection
  const updateCurrentTrackId = useCallback((id: string | null) => {
    if (id !== currentTrackId) {
      console.log(`ProgressContext: Track ID changing from ${currentTrackId} to ${id}`);
      setTrackIdJustChanged(true);
      
      // Reset the flag after a short delay
      setTimeout(() => {
        setTrackIdJustChanged(false);
      }, 500);
    }
    
    setCurrentTrackId(id);
  }, [currentTrackId]);
  
  // Function to reset all timing data for a new track
  const resetForNewTrack = useCallback((newTrackId: string, startMs: number, playing: boolean, timestamp?: number) => {
    console.log(`ProgressContext: HARD RESET for new track ${newTrackId} - clearing all state`);
    
    // HARD RESET: Clear everything completely
    
    // 1. Clear all timers immediately
    if (progressInterval.current) {
      window.clearInterval(progressInterval.current);
      progressInterval.current = null;
    }
    
    // 2. Reset ALL timer references
    lastResponseTimestamp.current = null;
    progressStartTime.current = null;
    
    // 3. Clear all manual states
    clearManualStates();
    
    // 4. Force immediate state reset
    setProgressMs(0);
    setIsPlaying(false);
    
    // 5. Update track ID (this will trigger useEffect cleanup)
    updateCurrentTrackId(newTrackId);
    
    // 6. Use setTimeout to ensure all state updates are processed before starting timer
    setTimeout(() => {
      console.log(`ProgressContext: Starting fresh timer for track ${newTrackId} at ${startMs}ms`);
      startProgressTimer(startMs, playing, timestamp);
    }, 100); // Longer delay for complete state cleanup
  }, [clearManualStates, updateCurrentTrackId, startProgressTimer]);
  
  // Function to reset timing without changing track ID (for drift correction)
  const resetTimingForSameTrack = useCallback((startMs: number, playing: boolean, timestamp?: number) => {
    console.log(`ProgressContext: Resetting timing for same track (drift correction) from ${progressMs}ms to ${startMs}ms`);
    
    // Clear any existing timer first
    if (progressInterval.current) {
      window.clearInterval(progressInterval.current);
      progressInterval.current = null;
    }
    
    // Reset timer references completely
    lastResponseTimestamp.current = null;
    progressStartTime.current = null;
    
    // Force immediate state update
    setProgressMs(startMs);
    setIsPlaying(playing);
    
    // Use setTimeout to ensure state has updated before starting timer
    setTimeout(() => {
      startProgressTimer(startMs, playing, timestamp);
    }, 0);
  }, [startProgressTimer]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (progressInterval.current) {
        window.clearInterval(progressInterval.current);
      }
    };
  }, []);
  
  // Reset manual states when track changes
  useEffect(() => {
    if (currentTrackId) {
      // Clear manual states and reset timer references to prevent drift
      setManualSelectedLyric(null);
      setManualLyricTimestamp(null);
      
      // Reset timer references for fresh start
      lastResponseTimestamp.current = null;
      progressStartTime.current = null;
      
      // Clear any existing timer to ensure clean restart
      if (progressInterval.current) {
        window.clearInterval(progressInterval.current);
        progressInterval.current = null;
      }
    }
  }, [currentTrackId]);
  
  // Reset manual states when progress is reset to near 0
  useEffect(() => {
    // If progress is reset to near 0 (e.g., when a song is restarted)
    // and we have a manual selection active, clear it but preserve the delay
    if (progressMs <= 50 && (manualSelectedLyric || manualLyricTimestamp)) {
      clearManualStates(true); // Preserve the delay
      console.log('Progress reset to beginning, clearing manual states but preserving delay');
    }
  }, [progressMs, manualSelectedLyric, manualLyricTimestamp, clearManualStates]);

  const contextValue = {
    progressMs,
    setProgressMs,
    startProgressTimer,
    resetForNewTrack,
    resetTimingForSameTrack,
    manualSelectedLyric,
    manualLyricTimestamp,
    setManualLyricSelection,
    userScrolling,
    setUserScrolling,
    clearManualStates,
    isPlaying,
    currentTrackId,
    setCurrentTrackId: updateCurrentTrackId,
    trackIdJustChanged,
    lyricsDelay,
    setLyricsDelay,
    getEffectiveProgress,
    isInManualMode
  };

  return (
    <ProgressContext.Provider value={contextValue}>
      {children}
    </ProgressContext.Provider>
  );
}

// Hook for consuming the context
export function useProgress() {
  return useContext(ProgressContext);
} 